import { useAuth } from "@dl/web-core-js/auth";
import { Button, Dialog, Field, Input, Switch, toast } from "@dl/web-core-js/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ServiceProductionClient } from "../../api/floor";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import type { ServiceOrderLineDTO } from "../../api/servicedesk";
import { AvailabilityBadge, useServiceAvailability } from "./ServicePartsPicker";

/** A line can go to the plant when something is left to send and no lot is making it yet. */
export function canMakeAtPlant(line: ServiceOrderLineDTO): boolean {
  return Number(line.quantity_outstanding ?? 0) > 0 && line.making_lot_id == null;
}

interface Row {
  line: ServiceOrderLineDTO;
  quantity: string;
}

/**
 * Sends service lines to the plant as one SERVICE lot.
 *
 * <p>One line per chosen service line: how many to make (what is left to send,
 * by default; never more), with the service shelf's availability beside it so
 * a line the shelf can cover is not made by mistake. Lines already being made,
 * or with nothing left to send, are listed as skipped. The server groups the
 * lines by part into the lot's components and links each line to its
 * component, all or none.</p>
 */
export function MakeAtPlantDialog({
  lines,
  orderNumbers,
  excludeOrderId,
  onClose,
}: {
  lines: ServiceOrderLineDTO[];
  /** Service order numbers by id, to say which order a line is from when several are mixed. */
  orderNumbers?: Map<number, number>;
  /** The order the lines come from, whose own lines must not hide the shelf stock. */
  excludeOrderId?: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const client = useMemo(() => new ServiceProductionClient(http), [http]);
  const eligible = lines.filter(canMakeAtPlant);
  const skipped = lines.filter((line) => !canMakeAtPlant(line));
  const [rows, setRows] = useState<Row[]>(() => eligible.map((line) => ({ line, quantity: String(Number(line.quantity_outstanding ?? 0)) })));
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");
  const [release, setRelease] = useState(true);
  const availability = useServiceAvailability(eligible.map((line) => line.part_id), excludeOrderId);

  const invalid = (row: Row) => {
    const quantity = Number(row.quantity);
    return !(quantity > 0) || quantity > Number(row.line.quantity_outstanding ?? 0);
  };
  const valid = rows.length > 0 && rows.every((row) => !invalid(row));
  const covered = rows.filter((row) => Number(availability.get(row.line.part_id)?.available ?? 0) >= Number(row.quantity));

  const create = useMutation({
    mutationFn: () =>
      client.create({
        lines: rows.map((row) => ({ service_order_line_id: row.line.id, quantity: Number(row.quantity) })),
        due_date: due || null,
        note: note.trim() === "" ? null : note.trim(),
        release,
      }),
    onSuccess: async (lot) => {
      await queryClient.invalidateQueries({ queryKey: ["servicedesk"] });
      await queryClient.invalidateQueries({ queryKey: ["production"] });
      onClose();
      toast.success(t("sd.plant.created", "Lot {{n}} created", { n: lot.number }), {
        action: {
          label: t("sd.plant.open-lot", "Open the lot"),
          onClick: () => void navigate({ to: "/production/orders/$orderId", params: { orderId: String(lot.id) } }),
        },
      });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const set = (index: number, quantity: string) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, quantity } : row)));

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title={t("sd.plant.title", "Make at the plant")}
      description={t(
        "sd.plant.description",
        "The parts become one service lot, followed on the floor like any other, and land on the service shelf when it is completed.",
      )}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={create.isPending} onClick={() => create.mutate()}>
            {t("sd.plant.create", "Create the lot")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {rows.length === 0 ? (
          <p className="text-[13px] text-ink-muted">{t("sd.plant.none", "None of the chosen lines can go to the plant.")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] text-ink-muted">
                  <th className="pb-2 pr-3 font-medium">{t("column.bom.component", "Part")}</th>
                  <th className="pb-2 pr-3 font-medium">{t("column.sd.shelf", "Service shelf")}</th>
                  <th className="pb-2 pr-3 font-medium">{t("sd.plant.left", "Left to send")}</th>
                  <th className="pb-2 font-medium">{t("sd.plant.make", "To make")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.line.id} className="border-t border-line align-middle">
                    <td className="py-2 pr-3">
                      <span className="font-medium">{row.line.part_number}</span>
                      <span className="ml-2 text-[11px] text-ink-faint">{row.line.part_name}</span>
                      {orderNumbers?.get(row.line.service_order_id) != null && (
                        <span className="ml-2 text-[11px] text-ink-faint">
                          {t("sd.plant.from-order", "order {{n}}", { n: orderNumbers.get(row.line.service_order_id) })}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <AvailabilityBadge availability={availability.get(row.line.part_id)} needed={Number(row.quantity)} />
                    </td>
                    <td className="py-2 pr-3 tabular-nums">{formatNumber(row.line.quantity_outstanding)}</td>
                    <td className="py-2">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        className="w-24"
                        aria-label={t("sd.plant.make", "To make")}
                        value={row.quantity}
                        invalid={invalid(row)}
                        onChange={(event) => set(index, event.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {covered.length > 0 && (
          <p className="rounded-md bg-warn-soft px-3 py-2 text-[12px] text-warn">
            {t("sd.plant.covered", "The service shelf already has enough for {{parts}}: ship from the shelf instead of making them.", {
              parts: covered.map((row) => row.line.part_number).join(", "),
            })}
          </p>
        )}
        {skipped.length > 0 && (
          <p className="text-[12px] text-ink-muted">
            {t("sd.plant.skipped", "Left out, already being made or with nothing left to send: {{parts}}.", {
              parts: skipped.map((line) => line.part_number).join(", "),
            })}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("column.mo.due", "Due")} htmlFor="sd-plant-due">
            <Input id="sd-plant-due" type="date" value={due} onChange={(event) => setDue(event.target.value)} />
          </Field>
          <Field label={t("form.field.note", "Note")} htmlFor="sd-plant-note">
            <Input id="sd-plant-note" value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        </div>
        <Switch checked={release} onChange={setRelease} label={t("sd.plant.release", "Release to the floor now")} />
      </div>
    </Dialog>
  );
}

/** "Lot 900123-2 · 40%": the lot making a line, as a link to it. */
export function MakingChip({ line }: { line: ServiceOrderLineDTO }) {
  const { t } = useTranslation();
  if (line.making_lot_id == null) return null;
  const percent = Math.round(Number(line.making_progress ?? 0) * 100);
  const done = line.making_lot_status === "COMPLETED";
  return (
    <Link
      to="/production/orders/$orderId"
      params={{ orderId: String(line.making_lot_id) }}
      onClick={(event) => event.stopPropagation()}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium hover:underline ${
        done ? "bg-ok-soft text-ok" : "bg-info-soft text-info"
      }`}
    >
      {done
        ? t("sd.plant.chip-done", "Lot {{code}} · made", { code: line.making_component_code ?? line.making_lot_number })
        : t("sd.plant.chip", "Lot {{code}} · {{percent}}%", { code: line.making_component_code ?? line.making_lot_number, percent })}
    </Link>
  );
}
